import { useCallback, useEffect, useRef, useState } from 'react';

type LoyaltyIdentity = string | null;

interface QueryResultWithData<TData> {
  data?: TData;
}

interface UseRefetchOnLoyaltyUserChangeOptions<TData, TResult extends QueryResultWithData<TData>> {
  loyaltyUserId?: string;
  queryData?: TData;
  queryError?: unknown;
  queryKey: string;
  queryLoading: boolean;
  refetch: () => Promise<TResult>;
  skip: boolean;
}

interface LoyaltyUserQueryIdentityBoundary<TData, TResult extends QueryResultWithData<TData>> {
  dataForCurrentIdentity?: TData;
  identityTransitionLoading: boolean;
  isDataForCurrentIdentity: boolean;
  refetchForCurrentIdentity: () => Promise<TResult>;
}

interface IdentityVersion {
  generation: number;
  identity: LoyaltyIdentity;
}

interface AcceptedQueryState<TData> {
  data?: TData;
  generation: number;
  hasUsableData: boolean;
  queryKey: string;
}

interface TrackedRequest {
  generation: number;
  queryKey: string;
  requestId: number;
}

interface AutomaticExecution {
  generation: number;
  queryKey: string;
}

/**
 * Isolates optional-auth Apollo data across loyalty identity changes.
 *
 * Apollo retains and mutates an observable's previous `data` while overlapping
 * requests settle. This boundary copies only data accepted for the latest
 * identity generation, so an older request cannot become visible after a rapid
 * A -> B -> A transition. A later automatic execution caused by query-variable
 * changes can also recover a failed boundary after its loading cycle settles.
 */
export function useRefetchOnLoyaltyUserChange<TData, TResult extends QueryResultWithData<TData>>({
  loyaltyUserId,
  queryData,
  queryError,
  queryKey,
  queryLoading,
  refetch,
  skip,
}: UseRefetchOnLoyaltyUserChangeOptions<TData, TResult>): LoyaltyUserQueryIdentityBoundary<
  TData,
  TResult
> {
  const currentIdentity = loyaltyUserId ?? null;
  const identityVersionRef = useRef<IdentityVersion>({ generation: 0, identity: currentIdentity });
  if (identityVersionRef.current.identity !== currentIdentity) {
    identityVersionRef.current = {
      generation: identityVersionRef.current.generation + 1,
      identity: currentIdentity,
    };
  }
  const currentGeneration = identityVersionRef.current.generation;

  const mountedRef = useRef(true);
  const refetchRef = useRef(refetch);
  const queryKeyRef = useRef(queryKey);
  const nextRequestIdRef = useRef(0);
  const latestRequestRef = useRef<TrackedRequest | null>(null);
  const automaticExecutionRef = useRef<AutomaticExecution | null>(null);
  const requestedIdentityGenerationRef = useRef(currentGeneration);
  const [acceptedQuery, setAcceptedQuery] = useState<AcceptedQueryState<TData>>(() => ({
    data: queryData,
    generation: currentGeneration,
    hasUsableData: true,
    queryKey,
  }));
  const acceptedQueryRef = useRef(acceptedQuery);

  refetchRef.current = refetch;
  queryKeyRef.current = queryKey;
  acceptedQueryRef.current = acceptedQuery;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetchForCurrentIdentity = useCallback(async () => {
    const targetIdentityVersion = identityVersionRef.current;
    const targetQueryKey = queryKeyRef.current;
    const requestId = nextRequestIdRef.current + 1;
    nextRequestIdRef.current = requestId;
    latestRequestRef.current = {
      generation: targetIdentityVersion.generation,
      queryKey: targetQueryKey,
      requestId,
    };
    automaticExecutionRef.current = null;
    const protectsIdentityBoundary =
      acceptedQueryRef.current.generation !== targetIdentityVersion.generation ||
      !acceptedQueryRef.current.hasUsableData;

    const isLatestCurrentRequest = () => {
      const latestRequest = latestRequestRef.current;
      return (
        mountedRef.current &&
        identityVersionRef.current.generation === targetIdentityVersion.generation &&
        latestRequest?.generation === targetIdentityVersion.generation &&
        latestRequest.requestId === requestId
      );
    };

    try {
      const result = await refetchRef.current();
      if (isLatestCurrentRequest()) {
        latestRequestRef.current = null;
        if (result.data !== undefined) {
          setAcceptedQuery({
            data: result.data,
            generation: targetIdentityVersion.generation,
            hasUsableData: true,
            queryKey: targetQueryKey,
          });
        } else if (protectsIdentityBoundary) {
          setAcceptedQuery({
            data: undefined,
            generation: targetIdentityVersion.generation,
            hasUsableData: false,
            queryKey: targetQueryKey,
          });
        }
      }
      return result;
    } catch (error) {
      if (isLatestCurrentRequest()) {
        latestRequestRef.current = null;
        if (protectsIdentityBoundary) {
          setAcceptedQuery({
            data: undefined,
            generation: targetIdentityVersion.generation,
            hasUsableData: false,
            queryKey: targetQueryKey,
          });
        }
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    if (skip || requestedIdentityGenerationRef.current === currentGeneration) {
      return;
    }
    requestedIdentityGenerationRef.current = currentGeneration;
    void refetchForCurrentIdentity().catch(() => undefined);
  }, [currentGeneration, refetchForCurrentIdentity, skip]);

  useEffect(() => {
    if (skip || acceptedQuery.generation !== currentGeneration) {
      automaticExecutionRef.current = null;
      return;
    }

    const latestRequest = latestRequestRef.current;
    if (latestRequest?.generation === currentGeneration) {
      automaticExecutionRef.current = null;
      return;
    }

    const shouldTrackExecution =
      acceptedQuery.queryKey !== queryKey ||
      acceptedQuery.data === undefined ||
      !acceptedQuery.hasUsableData;

    if (queryLoading) {
      if (shouldTrackExecution) {
        automaticExecutionRef.current = { generation: currentGeneration, queryKey };
      }
      return;
    }

    const automaticExecution = automaticExecutionRef.current;
    const canAcceptSettledQueryWithoutObservedLoading =
      !queryLoading &&
      !queryError &&
      queryData !== undefined &&
      (acceptedQuery.queryKey !== queryKey ||
        (acceptedQuery.hasUsableData && acceptedQuery.data === undefined));
    if (!automaticExecution && canAcceptSettledQueryWithoutObservedLoading) {
      setAcceptedQuery({
        data: queryData,
        generation: currentGeneration,
        hasUsableData: true,
        queryKey,
      });
      return;
    }

    if (
      automaticExecution?.generation !== currentGeneration ||
      automaticExecution.queryKey !== queryKey
    ) {
      return;
    }
    automaticExecutionRef.current = null;

    if (!queryError && queryData !== undefined) {
      setAcceptedQuery({
        data: queryData,
        generation: currentGeneration,
        hasUsableData: true,
        queryKey,
      });
    }
  }, [
    acceptedQuery.data,
    acceptedQuery.generation,
    acceptedQuery.hasUsableData,
    acceptedQuery.queryKey,
    currentGeneration,
    queryData,
    queryError,
    queryKey,
    queryLoading,
    skip,
  ]);

  const latestRequest = latestRequestRef.current;
  const hasCurrentIdentityRequest = latestRequest?.generation === currentGeneration;
  // Apollo can settle one render before the passive effect above copies its result into
  // `acceptedQuery`. The initial generation/key is safe to expose immediately; identity changes
  // and explicit refetches fail these guards and continue through the protected acceptance path.
  const canUseInitialSettledQueryData =
    !skip &&
    !hasCurrentIdentityRequest &&
    acceptedQuery.generation === currentGeneration &&
    acceptedQuery.queryKey === queryKey &&
    acceptedQuery.hasUsableData &&
    acceptedQuery.data === undefined &&
    !queryLoading &&
    !queryError &&
    queryData !== undefined;
  const isAcceptedDataForCurrentIdentity =
    acceptedQuery.generation === currentGeneration &&
    acceptedQuery.queryKey === queryKey &&
    acceptedQuery.hasUsableData;
  const isDataForCurrentIdentity =
    canUseInitialSettledQueryData || isAcceptedDataForCurrentIdentity;
  const identityTransitionLoading =
    !skip &&
    !canUseInitialSettledQueryData &&
    (acceptedQuery.generation !== currentGeneration ||
      (acceptedQuery.queryKey !== queryKey && !queryError));

  return {
    dataForCurrentIdentity: canUseInitialSettledQueryData
      ? queryData
      : isAcceptedDataForCurrentIdentity
      ? acceptedQuery.data
      : undefined,
    identityTransitionLoading,
    isDataForCurrentIdentity,
    refetchForCurrentIdentity,
  };
}
